import PropTypes from "prop-types"
import { useEffect, useState, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Alert, Button, Flex, Spin, Tooltip } from "antd"
import { CalendarOutlined, LeftOutlined, CloseOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { routes } from "router/routes"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { useCalendarBlackouts } from "pagesV2/pro/Calendar/hooks/useCalendarBlackouts"
import BlackoutConflictBanner from "./components/BlackoutConflictBanner"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import IconWithBg from "ui/IconWithBg"
import { DSIcon } from "ui/Icon/DSIcon"
import { NOTE_PARENTS } from "store/upcomingExperiences"
import NotesSection from "../BookingDetailsPage/BookingDetailSummary/NotesSection"
import ReservationSummaryCard from "./components/ReservationSummaryCard/ReservationSummaryCard"
import ClientDetailsCard from "./components/ClientDetailsCard/ClientDetailsCard"
import ExperienceCard from "./components/ExperienceCard/ExperienceCard"
import PaymentsPanel from "./components/PaymentsPanel/PaymentsPanel"
import BookingHistoryPanel from "./components/BookingHistoryPanel/BookingHistoryPanel"
import ResourcesPanel from "./components/ResourcesPanel/ResourcesPanel"
import CommunicationHistoryPanel from "./components/CommunicationHistoryPanel/CommunicationHistoryPanel"
import OrderExperience from "./components/OrderExperience/OrderExperience"
import { useOrderExperiences } from "./useOrderExperiences"
import { useOrderPaymentHistory } from "./useOrderPaymentHistory"
import { buildOrderPayments, getActiveOrderExperiences } from "./orderPayments"
import {
  fetchReservationDetail,
  fetchReservationWaivers,
  fetchReservationPaymentHistory,
  fetchReservationCommunications,
  fetchReservationAnswers,
  fetchReservationChangeHistory
} from "store/reservations"
import DeclineBookingModal from "../BookingActions/DeclineBookingModal"
import CancelBookingModal from "../BookingActions/CancelBookingModal"
import BookingConfirmationModal from "ui/BookingConfirmationModal"
import { guideBookingStatus, setClientId, cleanClientId } from "store/bookings"
import { deriveCheckInState } from "../CheckIn/checkInUtils"
import { isFeatureEnabled } from "config/features"
import styles from "./index.module.css"

// `uuid`/`isModal`/`onClose` let this same screen render inside a modal (e.g. from the calendar).
// As a routed page it reads the uuid from the URL and shows the breadcrumb + back nav.
const ReservationDetailPage = ({ onBack, uuid: uuidProp, isModal = false, onClose, onAssignmentsChange }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id: paramUuid } = useParams()
  const uuid = uuidProp ?? paramUuid
  const handleBack = onBack ?? (() => navigate(routes.guideReservationsPath()))
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false)
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const { isActive: isGuideActive } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const { data, loading } = useSelector((store) => store.reservations.detail)
  const { getBookingConflict } = useCalendarBlackouts()

  /**
   * The reservation states its own conflict — the server resolves it against this booking's crew
   * and equipment assignments. The calendar's feed is only consulted when the payload predates
   * the field, and matches by booking id rather than by experience title as it once did.
   * Deliberately no extra fetch: a one-day request would overwrite the month the calendar is
   * showing behind this modal.
   */
  const bookingDateKey = data?.date ? moment(data.date, ["MMM D, YYYY", DATE_FORMAT]).format(DATE_FORMAT) : null
  const blackoutConflict = data?.blackoutConflict || getBookingConflict({ id: data?.id, date: bookingDateKey })
  const waiversSlice = useSelector((store) => store.reservations.waivers)
  const paymentHistorySlice = useSelector((store) => store.reservations.paymentHistory)
  const communicationsSlice = useSelector((store) => store.reservations.communications)
  const answersSlice = useSelector((store) => store.reservations.answers)
  const changeHistorySlice = useSelector((store) => store.reservations.changeHistory)
  // guideBookingStatus runs through the bookings.booking loading flags, so Accept can spin off it.
  const isBookingStatusLoading = useSelector((store) => store.bookings.booking.loading)
  // Several experiences booked together (an order) show as one reservation, one accordion each.
  const { experiences, isOrder, loadExperience } = useOrderExperiences(data)
  // An order's Payments panel lists every experience's payments in one history.
  const { history: orderHistory, isLoading: isOrderHistoryLoading, reload: reloadOrderHistory } = useOrderPaymentHistory(experiences, isOrder)
  // Each experience of an order has its own crew and equipment, so the Resources panel lists them
  // per experience, in the accordions' order.
  const orderResourceBookings = experiences.map((entry) =>
    entry.uuid === data?.uuid
      ? { uuid: entry.uuid, title: data.experience?.title, date: bookingDateKey, conflict: blackoutConflict }
      : { uuid: entry.uuid, title: entry.data?.experience?.title || entry.booking?.title, date: entry.bookedDate, conflict: entry.data?.blackoutConflict || null }
  )

  const refreshDetail = useCallback(() => {
    if (uuid) dispatch(fetchReservationDetail(uuid))
  }, [uuid, dispatch])

  const refreshWaivers = useCallback(() => {
    if (uuid) dispatch(fetchReservationWaivers(uuid))
  }, [uuid, dispatch])

  // Editing the client can change their email, which the waiver rows carry (and use for
  // "Resend Waiver") — so a client edit has to refresh the waivers alongside the detail.
  const refreshClient = useCallback(() => {
    refreshDetail()
    refreshWaivers()
  }, [refreshDetail, refreshWaivers])

  // Resending a waiver logs a communication as well as updating the row, so the history panel
  // has to refetch alongside the waivers — otherwise the send only appears after a reload.
  const refreshWaiverNotification = useCallback(() => {
    refreshWaivers()
    if (uuid) dispatch(fetchReservationCommunications(uuid))
  }, [refreshWaivers, uuid, dispatch])

  // Payment-affecting actions change both the summary and the history list, and a price
  // adjustment also adds a booking change-history entry.
  const refreshPayments = useCallback(() => {
    refreshDetail()
    if (uuid) {
      dispatch(fetchReservationPaymentHistory(uuid))
      dispatch(fetchReservationChangeHistory(uuid))
    }
  }, [refreshDetail, uuid, dispatch])

  // Accepting/declining a request can capture or release the payment, always logs a status change,
  // and emails the client — so every panel fed by those endpoints has to refetch, not just the detail.
  const refreshRequestStatus = useCallback(() => {
    refreshPayments()
    if (uuid) dispatch(fetchReservationCommunications(uuid))
  }, [refreshPayments, uuid, dispatch])

  // "Show more" pages through the change-history endpoint, appending each next page.
  const loadMoreChangeHistory = useCallback(() => {
    const nextPage = changeHistorySlice.metadata?.next_page
    if (uuid && nextPage) dispatch(fetchReservationChangeHistory(uuid, { page: nextPage }, { append: true }))
  }, [uuid, dispatch, changeHistorySlice.metadata])

  useEffect(() => {
    refreshDetail()
  }, [refreshDetail])

  // Lazy sub-resources keyed by uuid
  useEffect(() => {
    if (!uuid) return
    dispatch(fetchReservationWaivers(uuid))
    dispatch(fetchReservationPaymentHistory(uuid))
    dispatch(fetchReservationAnswers(uuid))
    dispatch(fetchReservationCommunications(uuid))
    dispatch(fetchReservationChangeHistory(uuid))
  }, [uuid, dispatch])

  // Take Payment (ui/Booking/Details/…) reads the client from state.bookings.clientId, which
  // only the old booking-details body populates. Mirror that here so attaching a card sends the
  // real user_id (not new_user:true / id:null) and loads this client's saved payment methods.
  const detailUserId = data?.client?.user_id
  useEffect(() => {
    if (detailUserId) dispatch(setClientId(detailUserId))
    return () => dispatch(cleanClientId())
  }, [detailUserId, dispatch])

  // An experience of an order changed (canceled, price adjusted, paid…): reload it. The opened
  // booking is the page's own detail, so it refreshes with its payment and change history.
  const handleExperienceChange = (experienceUuid) => {
    if (experienceUuid === data?.uuid) refreshPayments()
    else loadExperience(experienceUuid)
    reloadOrderHistory(experienceUuid)
  }

  // The order's Payments panel names the experience a payment action changed; a whole-order action
  // (adjust price, cancel) changed them all.
  const handleOrderChange = (experienceUuid) => {
    if (experienceUuid) {
      handleExperienceChange(experienceUuid)
      return
    }
    experiences.forEach((entry) => (entry.uuid === data?.uuid ? refreshPayments() : loadExperience(entry.uuid)))
    reloadOrderHistory()
  }

  // The full-page spinner only while another reservation loads: a refresh of this one keeps the
  // page (and the accordions the guide opened) in place.
  if (!data || (loading && data.uuid !== uuid)) {
    return (
      <Flex justify="center" align="center" style={{ height: "55vh" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  // Core sections come from the detail payload; waivers/answers/payment-history/
  // communications are lazy-loaded from their own endpoints (slices above).
  const { client, experience, payments } = data

  // Check-in is read-only here — the status is derived for display; check-in itself happens
  // from the reservation/scheduled listings.
  const totalGuests = data.totalGuests ?? 0
  const checkedInCount = data.checkedInCount ?? 0
  // Honor the backend check_in_state when present (it carries the group's real status even when
  // the aggregate counts aren't sent); otherwise fall back to deriving from the counts.
  const checkInState = data.checkInState || deriveCheckInState(checkedInCount, totalGuests, data.checkedOutCount ?? 0)

  // A pending request can be approved (→ booked) or declined (→ canceled, with a reason).
  const isRequest = data.rawStatus === "requested"
  // Completed bookings can't be edited — the header offers "Book Again" instead (matches the
  // old booking detail page). Routes to the pre-filled new-booking flow for this reservation.
  const isCompleted = data.rawStatus === "completed"
  // Only an active (upcoming/booked) booking can be canceled — requests use Decline, and
  // completed/canceled bookings are terminal. Mirrors the old booking-details "Cancel Booking".
  const isCanceled = data.rawStatus === "canceled"
  // An order is cancelled as a whole (Cancel Reservation), once the API names the order; each
  // experience also has its own Cancel Experience.
  const canCancel = isOrder
    ? Boolean(data.orderUuid) && getActiveOrderExperiences(experiences).some((entry) => entry.data.canEdit && entry.data.rawStatus !== "requested")
    : data.canEdit && !isRequest && !isCompleted && !isCanceled
  // DS-4473. Post-trip charges: the backend decides eligibility (completion timestamp, refund and
  // dispute state, the configured adjustment window); the frontend only adds the permission check
  // it applies to every other money action.
  // A ticketed booking (it always has ticket lines) is edited from its departure and tickets (QA
  // Ticketing #57), where the API saves them on edit (TICKETED_EDIT); post-trip charges aren't
  // offered for it yet.
  const isTicketed = (experience?.ticketLines?.length ?? 0) > 0
  const canEditReservation = data.canEdit && (!isTicketed || isFeatureEnabled("TICKETED_EDIT"))
  const canAddPostTripCharges = Boolean(data.canAdjustPostTrip) && hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT) && !isTicketed
  // Shape the Cancel Booking modal expects: `id` (numeric trip-booking id) drives the payment
  // breakdown, `uuid` drives the cancel submit; the rest is display in its BookingCard.
  const cancelBookingData = {
    id: data.bookingId,
    uuid: data.uuid,
    // Set on an order: the modal then loads the order's breakdown and cancels the whole order.
    orderUuid: isOrder ? data.orderUuid : null,
    title: isOrder ? t("booking.reservation_detail.experiences_count", { count: experiences.length }) : data.experience?.title,
    image: isOrder ? null : data.experience?.imageUrl,
    booking_id: isOrder ? data.orderNumber : data.reservationId,
    booked_on: data.created,
    client_name: data.client?.name,
    client_email: data.client?.email
  }

  // Matches the legacy booking-details header: a request whose trip time has passed auto-expires
  // and can no longer be accepted or declined, and a deactivated guide can't act on requests at
  // all. Only the expiry case gets an explanatory tooltip (same copy as the old header).
  const isRequestActionDisabled = data.isExpired || !isGuideActive
  // DS-4473. An expired request the backend says is still recoverable: the guide picks a new slot
  // and accepts in one commit, instead of the request being a dead end. Accept/Decline stay
  // disabled — the original slot is gone, so acceptance only makes sense with a new date.
  const canRecoverRequest = isRequest && data.isExpired && Boolean(data.canRecover) && isGuideActive && hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)
  // Accept/Decline are shown inline (no dropdown/drawer) at every breakpoint. On mobile they
  // fill the row equally (via requestActionsRowMobile) so they match the full-width Edit button.
  // An order's requests are accepted or declined on their own experience, not in the header.
  const requestActions = isRequest && !isOrder && (
    <Tooltip title={data.isExpired && !canRecoverRequest ? t("client.client_booking.trip_time_has_passed") : ""} placement="top" mouseEnterDelay={0.3}>
      <Flex gap={8} className={`${styles.requestActionsRow} ${isMobile ? styles.requestActionsRowMobile : ""}`}>
        {canRecoverRequest ? (
          <Button
            shape="round"
            block={isMobile}
            type="primary"
            onClick={() => data.uuid && navigate(routes.guideBookingEditPath({ id: data.uuid }))}
          >
            {t("booking.recovery.action")}
          </Button>
        ) : (
          <>
            <Button
              shape="round"
              block={isMobile}
              variant="outlined"
              color="primary"
              disabled={isRequestActionDisabled}
              onClick={() => setIsDeclineModalOpen(true)}
            >
              {t("booking.reservation_detail.decline")}
            </Button>
            <Button shape="round" block={isMobile} type="primary" disabled={isRequestActionDisabled} onClick={() => setIsAcceptModalOpen(true)}>
              {t("booking.reservation_detail.accept")}
            </Button>
          </>
        )}
      </Flex>
    </Tooltip>
  )

  // One actions cluster shared by every header layout: stacks full-width on mobile, inline on
  // desktop. Book Again (completed) / Edit (editable) + inline request buttons (Accept/Decline).
  // An order keeps the reservation-level actions; Cancel and Adjust Price are also offered per
  // experience, and Accept/Decline only there, in each accordion.
  const headerActions = (canEditReservation || Boolean(requestActions) || isCompleted || canCancel) && (
    <Flex vertical={isMobile} align={isMobile ? "stretch" : "center"} gap={8} className={styles.headerActions}>
      {isCompleted ? (
        <>
          {/* DS-4473. Charges discovered after the trip — fuel, damage, an add-on used on the day —
              are made in the ordinary Edit Booking modal, which opens restricted for a completed
              booking. Backend-gated: without post_trip_adjustment_allowed a completed booking stays
              terminal exactly as before. */}
          {canAddPostTripCharges && (
            <Button
              shape="round"
              block={isMobile}
              variant="outlined"
              color="primary"
              onClick={() => data.uuid && navigate(routes.guideBookingEditPath({ id: data.uuid }))}
            >
              {t("booking.reservation_detail.edit_reservation")}
            </Button>
          )}
          <Button type="primary" block={isMobile} className={styles.editBtn} onClick={() => data.uuid && navigate(routes.guideBookingBookAgainPath({ id: data.uuid }))}>
            {t("booking.reservation_detail.book_again", "Book Again")}
          </Button>
        </>
      ) : (
        <>
          {/* Cancel Reservation sits before Edit — outlined red, opens the cancel modal. */}
          {canCancel && (
            <Button shape="round" block={isMobile} variant="outlined" color="danger" className={styles.cancelBtn} onClick={() => setIsCancelModalOpen(true)}>
              {t("booking.reservation_detail.cancel_reservation", "Cancel Reservation")}
            </Button>
          )}
          {/* A pending request only offers Accept/Decline; a canceled booking offers nothing. */}
          {canEditReservation && !isRequest && !isCanceled && (
            <Button type="primary" block={isMobile} className={styles.editBtn} onClick={() => data.uuid && navigate(routes.guideBookingEditPath({ id: data.uuid }))}>
              {t("booking.reservation_detail.edit_reservation")}
            </Button>
          )}
        </>
      )}
      {requestActions}
    </Flex>
  )

  // An order goes by its order number and its number of experiences; a single booking by its own
  // number, date and time.
  const reservationNumber = isOrder ? data.orderNumber : data.reservationId
  const headerMeta = isOrder
    ? t("booking.reservation_detail.experiences_count", { count: experiences.length })
    : `${data.date ?? ""}${data.timeStart ? ` | ${[data.timeStart, data.timeEnd].filter(Boolean).join(" - ")}` : ""}`

  const handleAccept = async () => {
    if (isSubmittingRequest) return
    setIsSubmittingRequest(true)
    try {
      await dispatch(guideBookingStatus(data.uuid, "booked"))
      setIsAcceptModalOpen(false)
      refreshRequestStatus()
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  const handleDecline = async (reason) => {
    if (isSubmittingRequest) return
    setIsSubmittingRequest(true)
    try {
      await dispatch(guideBookingStatus(data.uuid, "canceled", reason))
      setIsDeclineModalOpen(false)
      refreshRequestStatus()
    } finally {
      setIsSubmittingRequest(false)
    }
  }


  return (
    <Flex vertical gap={16} className={`${styles.page} ${isModal ? styles.pageModal : ""}`}>
      {isModal ? (
        /* Modal header: title + close, then a meta row with Edit + kebab (no breadcrumb/back) */
        <>
          <Flex align="center" justify="space-between" gap={8} className={styles.modalHeader}>
            <span className={styles.pageTitle}>{t("booking.reservation_detail.reservation_number", { id: reservationNumber })}</span>
            <IconWithBg icon={<CloseOutlined />} onClick={onClose} />
          </Flex>
          <Flex vertical={isMobile} align={isMobile ? "stretch" : "center"} justify="space-between" gap={12} className={styles.modalMetaRow}>
            <Flex align="center" gap={6}>
              <CalendarOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>{headerMeta}</span>
            </Flex>
            {headerActions}
          </Flex>
        </>
      ) : (
        <>
          {/* Breadcrumb */}
          <Flex align="center" gap={4} className={styles.breadcrumb}>
            <IconWithBg icon={<DSIcon name="reservations" width={20} height={20} fill="var(--color-text-primary)" />} onClick={handleBack} />
            <button type="button" className={styles.breadcrumbLink} onClick={handleBack}>
              {t("booking.reservation_detail.breadcrumb_reservations")}
            </button>
            <span className={styles.breadcrumbSep}>·</span>
            <span className={styles.breadcrumbCurrent}>{t("booking.reservation_detail.reservation_number", { id: reservationNumber })}</span>
          </Flex>

          {/* Page title row */}
          <Flex align="flex-start" justify="space-between" className={styles.titleRow}>
            <Flex align="flex-start" gap={8}>
              <IconWithBg className="mt-xxs" icon={<LeftOutlined />} onClick={handleBack} />
              <Flex vertical gap={4}>
                <span className={styles.pageTitle}>{t("booking.reservation_detail.reservation_number", { id: reservationNumber })}</span>
                <Flex align="center" gap={6}>
                  <CalendarOutlined className={styles.metaIcon} />
                  <span className={styles.metaText}>{headerMeta}</span>
                </Flex>
              </Flex>
            </Flex>
            {!isMobile && headerActions}
          </Flex>

          {/* Mobile action bar: full-width Edit + inline request actions + Cancel kebab */}
          {isMobile && <div className={styles.mobileActionBar}>{headerActions}</div>}
        </>
      )}

      {/* DS-4473. An expired request explains itself: either how to recover it, or the backend's
          reason it can't be. Silence here is what sent guides hunting for a disabled button. */}
      {!isOrder && isRequest && data.isExpired && (canRecoverRequest || data.recoveryIneligibleReason) && (
        <Alert
          type={canRecoverRequest ? "warning" : "info"}
          showIcon
          // No generic fallback: a null reason means the question doesn't apply, not that something
          // is wrong, so the banner stays off rather than inventing an error.
          message={canRecoverRequest ? t("booking.recovery.expired_banner") : data.recoveryIneligibleReason}
        />
      )}

      <BlackoutConflictBanner conflict={blackoutConflict} />

      {/* Body: left main + right sidebar (stacked single-column in modal) */}
      <Flex gap={16} align="flex-start" className={`${styles.body} ${isModal ? styles.bodyModal : ""}`}>
        {/* LEFT: main content */}
        <Flex vertical gap={12} className={styles.mainPanel}>
          <Flex gap={12} className={styles.topCards}>
            <ReservationSummaryCard reservation={data} orderNumber={isOrder ? data.orderNumber : null} />
            {client && <ClientDetailsCard client={client} bookingUuid={data.uuid} bookingId={data.bookingId} onRefresh={refreshClient} />}
          </Flex>
          {isOrder && experiences.map((entry) => <OrderExperience key={entry.uuid} entry={entry} onChange={handleExperienceChange} />)}
          {!isOrder && experience && (
            <ExperienceCard
              experience={experience}
              waivers={waiversSlice.data}
              waiversLoading={waiversSlice.loading}
              answers={answersSlice.data}
              answersLoading={answersSlice.loading}
              addOns={data.addOns}
              bookingUuid={data.uuid}
              onRefresh={refreshWaiverNotification}
              checkInState={checkInState}
              checkedInCount={checkedInCount}
              checkedOutCount={data.checkedOutCount ?? 0}
              totalGuests={totalGuests}
            />
          )}
        </Flex>

        {/* RIGHT: sidebar */}
        <Flex vertical gap={12} className={styles.sidePanel}>
          {isOrder && (
            <PaymentsPanel
              payments={buildOrderPayments(experiences)}
              reservation={data}
              history={orderHistory}
              historyLoading={isOrderHistoryLoading}
              onRefresh={handleOrderChange}
              collapsible={isModal}
              orderExperiences={experiences}
              orderUuid={data.orderUuid}
            />
          )}
          {!isOrder && payments && (
            <PaymentsPanel
              payments={payments}
              reservation={data}
              history={paymentHistorySlice.data}
              historyLoading={paymentHistorySlice.loading}
              onRefresh={refreshPayments}
              collapsible={isModal}
            />
          )}
          <BookingHistoryPanel
            history={changeHistorySlice.data}
            loading={changeHistorySlice.loading}
            hasMore={Boolean(changeHistorySlice.metadata?.next_page)}
            totalCount={changeHistorySlice.metadata?.total_results}
            onLoadMore={loadMoreChangeHistory}
            collapsible={isModal}
          />
          <ResourcesPanel
            bookingUuid={data.uuid}
            bookingDate={bookingDateKey}
            bookings={isOrder ? orderResourceBookings : null}
            collapsible={isModal}
            onAssignmentsChange={onAssignmentsChange}
            conflict={blackoutConflict}
          />
          {isModal ? (
            <NotesSection bookingId={data.uuid} parent={NOTE_PARENTS.reservation} collapsible />
          ) : (
            <div className={styles.notesPanel}>
              <NotesSection bookingId={data.uuid} parent={NOTE_PARENTS.reservation} />
            </div>
          )}
          <CommunicationHistoryPanel communications={communicationsSlice.data} loading={communicationsSlice.loading} collapsible={isModal} />
        </Flex>
      </Flex>

      {isAcceptModalOpen && (
        <BookingConfirmationModal
          open={isAcceptModalOpen}
          bookingConfirmStatus="accept"
          clientName={client?.name}
          tripDate={data.date}
          handleCancel={() => setIsAcceptModalOpen(false)}
          handleConfirm={handleAccept}
          loading={isBookingStatusLoading}
        />
      )}

      <DeclineBookingModal
        open={isDeclineModalOpen}
        onCancel={() => setIsDeclineModalOpen(false)}
        onConfirm={handleDecline}
        clientName={client?.name}
        tripDate={data.date}
        isSubmitting={isSubmittingRequest}
      />

      {isCancelModalOpen && (
        <CancelBookingModal
          open={isCancelModalOpen}
          onCancel={() => setIsCancelModalOpen(false)}
          onConfirm={() => {
            setIsCancelModalOpen(false)
            if (isOrder) handleOrderChange()
            else refreshDetail()
          }}
          bookings={[cancelBookingData]}
        />
      )}
    </Flex>
  )
}

ReservationDetailPage.propTypes = {
  onBack: PropTypes.func,
  uuid: PropTypes.string,
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  // Fired after a crew/equipment assign or unassign succeeds, so a host rendering this screen
  // over its own data (the equipment calendar) can refetch instead of going stale until reload.
  onAssignmentsChange: PropTypes.func
}

export default ReservationDetailPage
